import { IAuthServiceV2 } from '@volkswagen-onehub/authservice';
import { IPersonalDataResponse, WeProfileFacade } from './WeProfileFacade';

export interface IProfileServiceV2 {
    isUserProfileComplete(): Promise<boolean>;

    getUserProfile(): Promise<IUserProfile>;

    updateUserProfile(salutation: string, firstName: string, lastName: string): Promise<any>;

    getMBBStatusData(): Promise<IMBBStatusData>;
}

export interface IUserProfile {
    firstName?: string;
    lastName?: string;
    salutation?: string;
    dateOfBirth?: string;
    address?: {
        country?: string;
        city?: string;
        street?: string;
        housenumber?: string;
        zipCode?: string;
        primary?: boolean;
    };
    phoneNumber?: string;
    preferredLanguages?: string[];
}

export interface IMBBStatusData {
    profileCompleted: boolean;
    spinDefined: boolean;
    carnetEnrollmentCountry: string;
    mbbUserId: string;
}

export type User = {
    email: string;
    email_verified: boolean;
    family_name: string;
    given_name: string;
    name: string;
    picture: string;
    sub: string;
    updated_at: number;
};

export class ProfileServiceV2Impl implements IProfileServiceV2 {
    private readonly weProfileFacade: WeProfileFacade;

    public constructor(private readonly authService: IAuthServiceV2, stage: string) {
        this.weProfileFacade = new WeProfileFacade(this.authService, stage);
    }

    public async isUserProfileComplete(): Promise<boolean> {
        const userProfile: IUserProfile = await this.getUserProfile();
        return (
            userProfile !== undefined &&
            this.isNotBlank(userProfile.firstName) &&
            this.isNotBlank(userProfile.lastName) &&
            ['SALUTATION:MR', 'SALUTATION:MRS', 'SALUTATION:MS', undefined].some((s) => s === userProfile.salutation)
        );
    }

    public async getUserProfile(): Promise<IUserProfile> {
        const personalDataResponse = await this.weProfileFacade.get<IPersonalDataResponse>('personalData');
        return this.convertPersonalDataResponse(personalDataResponse);
    }

    public async getMBBStatusData(): Promise<IMBBStatusData> {
        return await this.weProfileFacade.get<IMBBStatusData>('mbbStatusData');
    }

    public async updateUserProfile(salutation: string, firstName: string, lastName: string): Promise<any> {
        const patchOperations: any = [];
        if (salutation) {
            patchOperations.push({ op: 'replace', path: '/salutation', value: salutation });
        } else {
            patchOperations.push({ op: 'remove', path: '/salutation' });
        }
        patchOperations.push(
            { op: 'replace', path: '/firstName', value: firstName },
            { op: 'replace', path: '/lastName', value: lastName },
        );
        return this.weProfileFacade.patch('personalData', patchOperations);
    }



    private isNotBlank(value: string | undefined): boolean {
        return !!(value && value.trim());
    }

    private convertPersonalDataResponse(responseData: IPersonalDataResponse): IUserProfile {
        const { addresses, phoneNumbers, ...rest } = responseData;
        const primaryPhoneNumber = phoneNumbers && phoneNumbers.find((phoneNumber) => phoneNumber.primary === true);

        return {
            ...rest,
            address: addresses && addresses.find((address) => address.primary === true),
            phoneNumber: primaryPhoneNumber && primaryPhoneNumber.number,
        };
    }
}
